import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import JSZip from "jszip";

import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Divider from "@mui/material/Divider";

import { GridActionsCellItem } from "@mui/x-data-grid";

import ResponsiveGrid from "../grid/responsiveGrid";

import AirspaceCharacterizationReportCreator from "./airspaceCharacterization/airspaceCharacterizationReportCreator";
import AirspaceCharacterizationReport from "./airspaceCharacterization/airspaceCharacterizationReport";

import HimsAnalysisCreator from "./hims/himsAnalysisCreator";
import HimsAnalysisReport from "./hims/himsAnalysisReport";

import ZeroConflictAnalysisCreator from "./zeroConfliction/zeroConflictAnalysisCreator";
import ZeroConflictAnalysisReport from "./zeroConfliction/zeroConflictAnalysisReport";

import { CustomPagination } from "../customPagination";
import { useUserAuth } from "../contexts/authContext";

const ReportType = {
    AIRSPACE_CHARACTERIZATION: {
        name: "Airspace Report",
        hasPermission: (user) => user.user_role === "Admin",
        creator: ({ setCreatingReportType, getReports }) => (
            <AirspaceCharacterizationReportCreator setCreatingReportType={setCreatingReportType} getReports={getReports} />
        ),
        report: ({ selectedReport, setSelectedReport }) => (
            <AirspaceCharacterizationReport selectedReport={selectedReport} setSelectedReport={setSelectedReport} />
        )
    },
    HIMS: {
        name: "HIMS Report",
        hasPermission: (user) => user.user_role === "Admin" || user.user_role === "Airspace Manager",
        report: ({ selectedReport, setSelectedReport }) => <HimsAnalysisReport selectedReport={selectedReport} setSelectedReport={setSelectedReport} />
    },
    ZERO_CONFLICT: {
        name: "ZC Report",
        hasPermission: (user) =>
            user.user_role === "Admin" ||
            user.user_role === "Airspace Manager" ||
            user.user_role === "Operations Manager" ||
            user.user_role === "Operator" ||
            user.user_role === "Admin - Org",
        report: ({ selectedReport, setSelectedReport }) => <ZeroConflictAnalysisReport selectedReport={selectedReport} setSelectedReport={setSelectedReport} />
    }
};

export default function AnalysisDashboard() {
    const [reports, setReports] = useState([]);
    const [selectedReport, setSelectedReport] = useState(null);
    const [creatingReportType, setCreatingReportType] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const { user, socket } = useUserAuth();

    const columns = [
        {
            field: "report_type",
            headerName: "Report Type",
            minWidth: 175,
            flex: 1,
            valueFormatter: (value) => {
                if (!value) {
                    return;
                }

                const words = value.split("_");
                let type = "";
                for (let i = 0; i < words.length; i++) {
                    type += words[i].charAt(0).toUpperCase() + words[i].substring(1).toLowerCase() + (i < words.length - 1 ? " " : "");
                }
                return type;
            }
        },
        {
            field: "date_created",
            headerName: "Date Created",
            minWidth: 175,
            flex: 1,
            editable: false,
            valueFormatter: (value) => `${dayjs(value).format("MMMM D, YYYY [at] h:mm a")}`
        },
        {
            field: "date_updated",
            headerName: "Date Updated",
            minWidth: 175,
            flex: 1,
            editable: false,
            valueFormatter: (value) => {
                if (value) {
                    return `${dayjs(value).format("MMMM D, YYYY [at] h:mm a")}`;
                } else {
                    return "";
                }
            }
        },
        {
            field: "report_expiration",
            headerName: "Expiration Date",
            minWidth: 175,
            flex: 1,
            editable: false,
            valueGetter: (value) => {
                if (value) {
                    return `${dayjs(value).format("MMMM D, YYYY [at] h:mm a")}`;
                } else {
                    return "";
                }
            }
        },
        {
            type: "actions",
            field: "actions",
            minWidth: 125,
            headerName: "Actions",
            cellClassName: "actions",
            getActions: ({ row }) => {
                const actions = [];
                if (row.report_file_exists) {
                    actions.push(
                        <Tooltip key={1} title="Download Zip Folder">
                            <GridActionsCellItem icon={<DownloadIcon />} label="Download" onClick={() => handleDownloadReport(row)} />
                        </Tooltip>
                    );
                }
                if (row.report_json || row.report_file_exists) {
                    actions.push(
                        <Tooltip key={2} title="View">
                            <GridActionsCellItem icon={<VisibilityIcon />} label="View" onClick={() => handleViewReport(row)} />
                        </Tooltip>
                    );
                }
                actions.push(
                    <Tooltip key={3} title="Delete">
                        <GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={() => handleDeleteReport(row)} />
                    </Tooltip>
                );
                return actions;
            }
        }
    ];

    useEffect(() => {
        if (!user || !socket) {
            return;
        }

        getReports();
        socket.on("analysis_update", () => getReports());
    }, [user, socket]);

    useEffect(() => {
        setExpanded(false);
    }, [creatingReportType]);

    const getReports = () => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        fetch(`api/analysis/get/${user.id}`, requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((records) => setReports(records))
            .catch((err) => console.error(err));
    };

    const getReportFile = async (uuid) => {
        let file = null;

        try {
            const requestOptions = {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            };
            const response = await fetch(`api/analysis/getFile/${uuid}`, requestOptions);
            if (response.ok) {
                const json = await response.json();
                file = json.report_file;
            }
        } catch (err) {
            console.error(err);
        }

        if (!file) {
            alert("The analysis contains no data.");
            return null;
        }

        const zip = await JSZip.loadAsync(file.data);
        if (Object.keys(zip.files).length === 0) {
            alert("The zip file of your analysis contains no data.");
            return null;
        }

        return file;
    };

    const handleDownloadReport = async (report) => {
        const file = await getReportFile(report.uuid);
        if (!file) {
            return;
        }

        const blob = new Blob([new Uint8Array(file.data)], { type: "application/zip" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `${report.uuid}.zip`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleViewReport = async (report) => {
        if (report.report_type === "AIRSPACE_CHARACTERIZATION") {
            setSelectedReport(report);
            return;
        }

        const file = await getReportFile(report.uuid);
        if (file) {
            setSelectedReport({ ...report, report_file: file });
        }
    };

    const handleDeleteReport = (report) => {
        if (!confirm("Are you sure you want to delete this Analysis Report?")) {
            return;
        }

        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" }
        };
        fetch(`api/analysis/delete/${report.uuid}`, requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => getReports())
            .catch((err) => console.error(err));
    };

    if (creatingReportType && creatingReportType === "AIRSPACE_CHARACTERIZATION") {
        const Creator = ReportType[creatingReportType].creator;
        return <Creator setCreatingReportType={setCreatingReportType} getReports={getReports} />;
    }

    if (selectedReport && selectedReport.report_type) {
        const Report = ReportType[selectedReport.report_type].report;
        return <Report selectedReport={selectedReport} setSelectedReport={setSelectedReport} />;
    }

    return (
        <Box sx={{ p: 3, height: "100%", width: "100%" }}>
            <Typography component="h2" variant="h6" color="#6b778c" noWrap>
                Analysis Dashboard
            </Typography>

            <Paper sx={{ p: 2, mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ height: "578px" }}>
                    <ResponsiveGrid
                        primaryColumns={["report_type", "actions"]}
                        rows={reports}
                        columns={columns}
                        disableRowSelectionOnClick
                        pagination
                        slots={{ pagination: CustomPagination }}
                        getRowId={({ uuid }) => uuid}
                        initialState={{ pagination: { paginationModel: { pageSize: 9 } } }}
                    />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} disableGutters sx={{ backgroundColor: "#90caf9" }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ minHeight: "20px", "& > .MuiAccordionSummary-expandIconWrapper": { color: "rgba(0, 0, 0, 0.87)" } }}
                        >
                            <Typography sx={{ color: "rgba(0, 0, 0, 0.87)", fontWeight: "500", fontSize: "13px" }}>CREATE REPORT</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ display: "flex", flexDirection: "column", p: 0 }}>
                            <Divider sx={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }} />
                            {Object.keys(ReportType)
                                .filter((type) => ReportType[type].hasPermission(user))
                                .map((type) => (
                                    <Button key={type} onClick={() => setCreatingReportType(type)} variant="contained">
                                        {ReportType[type].name}
                                    </Button>
                                ))}
                        </AccordionDetails>
                    </Accordion>
                </Box>

                {creatingReportType === "HIMS" && <HimsAnalysisCreator setCreatingReportType={setCreatingReportType} getReports={getReports} />}

                {creatingReportType === "ZERO_CONFLICT" ? (
                    <ZeroConflictAnalysisCreator setCreatingReportType={setCreatingReportType} setReports={setReports} />
                ) : (
                    <></>
                )}
            </Paper>
        </Box>
    );
}
